class DotoPromoCarousel {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-carousel-track]');
    this.cards = Array.from(root.querySelectorAll('[data-carousel-card]'));
    this.dotsRoot = root.querySelector('[data-carousel-dots]');
    this.prev = root.querySelector('[data-carousel-prev]');
    this.next = root.querySelector('[data-carousel-next]');
    this.autoplay = root.dataset.autoplay === 'true';
    this.loop = root.dataset.loop === 'true';
    this.pauseHover = root.dataset.pauseHover === 'true';
    this.delay = (Number.parseInt(root.dataset.autoplaySpeed || '5', 10) || 5) * 1000;
    this.timer = null;
    this.scrollRaf = null;
    this.resumeTimer = null;

    if (!this.track || this.cards.length === 0) return;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPrev = () => this.scrollPages(-1);
    this.onNext = () => this.scrollPages(1);
    this.onMouseEnter = () => this.stop();
    this.onMouseLeave = () => this.start();
    this.onInteraction = () => {
      this.stop();
      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => this.start(), Math.max(this.delay, 2500));
    };

    this.bind();
    this.refresh();
    this.start();
  }

  bind() {
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('pointerdown', this.onInteraction, { passive: true });
    this.track.addEventListener('touchstart', this.onInteraction, { passive: true });
    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.scrollPages(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.scrollPages(1);
      }
    });

    this.prev?.addEventListener('click', this.onPrev);
    this.next?.addEventListener('click', this.onNext);
    window.addEventListener('resize', this.onResize, { passive: true });

    if (this.pauseHover) {
      this.root.addEventListener('mouseenter', this.onMouseEnter);
      this.root.addEventListener('mouseleave', this.onMouseLeave);
    }
  }

  get pageSize() {
    if (!this.cards[0]) return this.track.clientWidth;
    const styles = getComputedStyle(this.track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const cardWidth = this.cards[0].getBoundingClientRect().width + gap;
    const visible = Math.max(1, Math.round(this.track.clientWidth / cardWidth));
    return cardWidth * visible;
  }

  get pages() {
    if (this.track.scrollWidth <= this.track.clientWidth + 2) return 1;
    return Math.max(1, Math.ceil(this.track.scrollWidth / this.track.clientWidth));
  }

  get currentPage() {
    if (this.pages <= 1) return 0;
    const max = Math.max(1, this.track.scrollWidth - this.track.clientWidth);
    return Math.min(this.pages - 1, Math.round((this.track.scrollLeft / max) * (this.pages - 1)));
  }

  refresh() {
    this.buildDots();
    this.updateControls();
  }

  buildDots() {
    if (!this.dotsRoot) return;
    this.dotsRoot.innerHTML = '';

    for (let index = 0; index < this.pages; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = '#doto-promo-carousel-dot';
      dot.setAttribute('aria-label', `Ir a grupo ${index + 1}`);
      dot.addEventListener('click', () => {
        this.goToPage(index);
        this.onInteraction();
      });
      this.dotsRoot.appendChild(dot);
    }

    this.updateDots();
  }

  goToPage(index) {
    const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const left = this.pages <= 1 ? 0 : (max * index) / (this.pages - 1);
    this.track.scrollTo({ left, behavior: 'smooth' });
  }

  updateDots() {
    if (!this.dotsRoot) return;
    Array.from(this.dotsRoot.children).forEach((dot, index) => {
      const active = index === this.currentPage;
      dot.classList.toggle('@active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  updateControls() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 2;
    const atEnd = this.track.scrollLeft >= max - 2;

    if (this.prev) this.prev.disabled = !this.loop && atStart;
    if (this.next) this.next.disabled = !this.loop && atEnd;

    this.updateDots();
  }

  onScroll() {
    if (this.scrollRaf) return;
    this.scrollRaf = window.requestAnimationFrame(() => {
      this.scrollRaf = null;
      this.updateControls();
    });
  }

  onResize() {
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => this.refresh(), 120);
  }

  scrollPages(direction) {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 2;
    const atEnd = this.track.scrollLeft >= max - 2;

    if (direction < 0 && atStart && this.loop) {
      this.track.scrollTo({ left: max, behavior: 'smooth' });
    } else if (direction > 0 && atEnd && this.loop) {
      this.track.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      this.track.scrollBy({ left: this.pageSize * direction, behavior: 'smooth' });
    }

    this.onInteraction();
  }

  advance() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    if (max <= 2) return;

    if (this.track.scrollLeft >= max - 4) {
      if (this.loop) this.track.scrollTo({ left: 0, behavior: 'smooth' });
      else this.stop();
      return;
    }

    this.track.scrollBy({ left: this.pageSize, behavior: 'smooth' });
  }

  start() {
    this.stop();
    if (!this.autoplay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.timer = window.setInterval(() => this.advance(), this.delay);
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }

  revealBlock(blockId) {
    const card = this.root.querySelector(`[data-block-id="${blockId}"]`);
    if (!card) return;
    this.stop();
    card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  destroy() {
    this.stop();
    window.clearTimeout(this.resumeTimer);
    window.clearTimeout(this.resizeTimer);
    if (this.scrollRaf) window.cancelAnimationFrame(this.scrollRaf);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('pointerdown', this.onInteraction);
    this.track?.removeEventListener('touchstart', this.onInteraction);
    this.prev?.removeEventListener('click', this.onPrev);
    this.next?.removeEventListener('click', this.onNext);
    window.removeEventListener('resize', this.onResize);
    this.root.removeEventListener('mouseenter', this.onMouseEnter);
    this.root.removeEventListener('mouseleave', this.onMouseLeave);
  }
}

const dotoPromoCarouselInstances = new Map();

function initDotoPromoCarousel(root) {
  if (!root || dotoPromoCarouselInstances.has(root)) return;
  dotoPromoCarouselInstances.set(root, new DotoPromoCarousel(root));
}

function destroyDotoPromoCarousel(root) {
  dotoPromoCarouselInstances.get(root)?.destroy();
  dotoPromoCarouselInstances.delete(root);
}

document.querySelectorAll('[data-doto-promo-carousel]').forEach(initDotoPromoCarousel);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll?.('[data-doto-promo-carousel]').forEach(initDotoPromoCarousel);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll?.('[data-doto-promo-carousel]').forEach(destroyDotoPromoCarousel);
});

document.addEventListener('shopify:block:select', (event) => {
  const root = event.target.closest?.('[data-doto-promo-carousel]');
  if (!root) return;
  initDotoPromoCarousel(root);
  dotoPromoCarouselInstances.get(root)?.revealBlock(event.detail?.blockId || event.target.dataset.blockId);
});
